// Drives the Committee shell's single content <iframe> WITHOUT polluting the
// browser's joint session history.
//
// The parent shell (CommitteeCockpitLive) owns ALL navigation history: every
// public /committee/... URL is a top-window history entry, and the frame is a
// puppet that only ever mirrors @frame_path. Binding `src={@frame_path}` and
// letting morphdom patch the attribute would perform a *real* iframe navigation
// on every change — each one adding a joint-history entry and reloading the
// frame even when it is already showing that URL. Instead the iframe is marked
// `phx-update="ignore"` (LiveView never touches it after the initial render) and
// the server pushes `committee_drive_frame` with the desired path; this hook
// drives it via `contentWindow.location.replace`, which never adds a history
// entry. Combined with the frame-side bridge (which converts the frame's own
// pushState to replaceState), Back/Forward map 1:1 to the parent's entries.
const IGNORED_QUERY_KEYS = ["committee_surface", "committee_origin", "id", "user_id"]

// Normalize a URL for comparison: absolute path + query with the frame-plumbing
// keys stripped. Two URLs that differ only by committee_surface/committee_origin
// (e.g. the frame self-navigated to `?committee_surface=true` while the parent
// recomputed `?committee_surface=true&committee_origin=workspace`) compare EQUAL,
// so the hook never triggers a redundant reload of a frame already in place.
function normalizePath(raw) {
  try {
    const url = new URL(raw, window.location.origin)
    const params = new URLSearchParams(url.search)
    IGNORED_QUERY_KEYS.forEach(key => params.delete(key))
    // Sort before comparing: the server rebuilds the frame query from a map
    // (`CommitteeNavigation.frame_path/2`), whose key order is not the order
    // the frame's own live-patch produced. Comparing unsorted made two URLs
    // that name the SAME destination look different, and the hook answered
    // with `location.replace` — a real iframe reload that remounted the
    // LiveView and threw away whatever the frame had just navigated to.
    params.sort()
    const query = params.toString()
    return `${url.pathname}${query ? `?${query}` : ""}`
  } catch (_error) {
    return null
  }
}

const CommitteeResourceFrame = {
  mounted() {
    this.lastDriven = null

    this.drive = path => {
      if (!path) return

      let target
      try {
        target = new URL(path, window.location.origin)
      } catch (_error) {
        return
      }
      // Never let the shell drive its content frame off-origin.
      if (target.origin !== window.location.origin) return

      const normalizedTarget = normalizePath(target.href)
      if (!normalizedTarget) return

      // The frame may have navigated ITSELF here (its own push_navigate, which
      // the parent then mirrored back as this drive). Reading the live location
      // avoids a redundant reload in that case. Same-origin, so this won't throw
      // in practice; guard anyway for the mid-load window.
      let currentHref
      try {
        currentHref = this.el.contentWindow.location.href
      } catch (_error) {
        currentHref = null
      }

      // This iframe has no `src`: it only ever holds a document because we put
      // one there. Anything that RELOADS the element — and moving an iframe in
      // the DOM does exactly that, which is what happens when a conditional
      // sibling enters or leaves — drops it back to `about:blank`. In that
      // state `lastDriven` is a lie, so it must not short-circuit us, or the
      // shell shows a blank frame forever.
      const lostDocument = !currentHref || currentHref === "about:blank"

      // Already the last path we drove (or the initial load) — nothing to do.
      if (!lostDocument && this.lastDriven === normalizedTarget) return

      if (!lostDocument && normalizePath(currentHref) === normalizedTarget) {
        this.lastDriven = normalizedTarget
        return
      }

      try {
        // replace() (not assignment) so the frame swap never adds a joint
        // session-history entry — the parent already owns the history entry.
        this.el.contentWindow.location.replace(target.href)
      } catch (_error) {
        this.el.src = target.href
      }
      this.lastDriven = normalizedTarget
    }

    // Primary mechanism: `data-frame-path` is the current frame URL, patched by
    // LiveView whenever @frame_path changes (rail clicks, frame self-navs the
    // parent mirrored, browser Back — all go through handle_params). mounted()
    // performs the initial load; updated() re-drives on every change.
    // Rede de segurança: um iframe recarregado por movimentação no DOM dispara
    // `load` em `about:blank` sem que a LiveView patcheie nada — nenhum
    // `updated()` chega. Só o próprio elemento sabe que perdeu o documento.
    this.onFrameLoad = () => {
      let href
      try {
        href = this.el.contentWindow.location.href
      } catch (_error) {
        href = null
      }

      if (!href || href === "about:blank") {
        this.lastDriven = null
        this.drive(this.el.getAttribute("data-frame-path"))
      }
    }

    this.el.addEventListener("load", this.onFrameLoad)

    this.drive(this.el.getAttribute("data-frame-path"))

    // Secondary: an explicit push_event re-assert for the case where the parent
    // short-circuits handle_params (top URL already correct) but still wants to
    // reassert the frame in case its own navigation drifted.
    this.handleEvent("committee_drive_frame", ({path}) => this.drive(path))
  },

  updated() {
    this.drive(this.el.getAttribute("data-frame-path"))
  },

  destroyed() {
    this.el.removeEventListener("load", this.onFrameLoad)
  },
}

export default CommitteeResourceFrame
